/**
 * Impersonation Modal
 *
 * Modal for Account Admins to select a user in their tenant to act as.
 * Uses the User API (same account) so the selected id is the backend User id for X-Act-As-User-Id.
 */

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search, User, UserCheck, Shield } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/components/atoms/input";
import { Button } from "@/shared/components/atoms/button";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { useImpersonationStore } from "../model/impersonationStore";
import { useWorkRoleStore } from "../model/workRoleStore";
import { userScopeApi } from "@/entities/auth";
import { useUserScopeStore } from "@/features/auth/model/user-scope.store";
import { useUsersImpersonationListQuery, userByIdQueryOptions } from "@entities/user";
import { type UserWorkContext } from "@entities/user";
import { backendRoleToWorkContext } from "../utils/role-mapping";
import type { User as AppUser } from "@entities/user";
import { getDisplayName } from "@entities/user";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/model/auth.store";

function userToWorkContext(user: AppUser): UserWorkContext {
  const { work_role, system_role } = backendRoleToWorkContext(user.role);
  return {
    id: user.id,
    account_id: user.tenant_id || "",
    branch_id: user.role === "LocationUser" ? (user.locationIds?.[0] ?? undefined) : undefined,
    work_role,
    system_role,
    assigned_queue_ids: [],
    backendRole: user.role,
  };
}

function getRoleLabel(
  role: string,
  translate: (key: string, defaultValue: string) => string
): string {
  switch (role) {
    case "AccountAdmin":
      return translate("pages:users.roles.accountAdmin", "Account Admin");
    case "TechnicalManager":
      return translate("pages:users.roles.technicalManager", "Technical Manager");
    case "TechnicalUser":
      return translate("pages:users.roles.technicalUser", "Technical User");
    case "LocationUser":
      return translate("pages:users.roles.locationUser", "Location User");
    case "User":
    default:
      return translate("pages:users.roles.user", "User");
  }
}

export function ImpersonationModal() {
  const { t } = useTranslation(["pages", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const { user: currentUser } = useAuthStore();

  const { isModalOpen, closeModal, startImpersonation, canImpersonate } = useImpersonationStore();

  const { currentContext, getSystemRole, getAccountId } = useWorkRoleStore();
  const { setScope, clearScope } = useUserScopeStore();

  const { data: usersResponse, isLoading: loading } = useUsersImpersonationListQuery({
    enabled: isModalOpen,
  });

  const filteredUsers = useMemo(() => {
    const users = usersResponse?.data ?? [];
    const accountId = getAccountId();
    return users
      .filter((user) => {
        if (user.tenant_id && user.tenant_id !== accountId) return false;
        if (user.id === currentContext?.id) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          return (
            getDisplayName(user).toLowerCase().includes(q) ||
            (user.email || "").toLowerCase().includes(q)
          );
        }
        if (user.id === currentUser?.id) return false;
        return true;
      })
      .sort((a, b) => {
        const roleOrder: Record<string, number> = {
          AccountAdmin: 0,
          TechnicalManager: 1,
          TechnicalUser: 2,
          User: 3,
          LocationUser: 4,
        };
        const aOrder = roleOrder[a.role] ?? 5;
        const bOrder = roleOrder[b.role] ?? 5;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return getDisplayName(a).localeCompare(getDisplayName(b));
      });
  }, [usersResponse?.data, searchQuery, getAccountId, currentContext?.id, currentUser?.id]);

  const handleSelectUser = async (user: AppUser) => {
    // Build real admin context from work store or auth user (currentContext can be null after logout persist)
    const realAdminContext =
      currentContext ?? (currentUser ? userToWorkContext(currentUser as unknown as AppUser) : null);
    if (!realAdminContext) return;

    const userWithDetails =
      user.role === "LocationUser"
        ? await queryClient.fetchQuery(userByIdQueryOptions(user.id)).catch(() => user)
        : user;

    const targetContext = userToWorkContext(userWithDetails);
    const targetName =
      getDisplayName(userWithDetails) || userWithDetails.email || userWithDetails.id;
    const targetRole = getRoleLabel(userWithDetails.role, (k, fb) => t(k, fb));

    startImpersonation({
      targetContext,
      targetUserName: targetName,
      targetUserRole: targetRole,
      realAdminContext,
    });

    try {
      const scope = await userScopeApi.buildUserScope();
      setScope(scope);
    } catch {
      clearScope();
    }

    closeModal();
    toast.success(t("pages:impersonation.started", "Impersonation started"), {
      description: t("pages:impersonation.actingAs", "You are now acting as {{name}}", {
        name: targetName,
      }),
    });

    queryClient.clear();

    if (userWithDetails.role === "LocationUser") {
      const primaryLocationId = userWithDetails.locationIds?.[0];
      navigate(primaryLocationId ? `/branches/${primaryLocationId}` : "/");
    }
  };

  if (!canImpersonate(getSystemRole())) {
    return null;
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            {t("pages:impersonation.title", "Open as user")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "pages:impersonation.description",
              "Select a user in your account to act as them. Menu and permissions will match that user."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("pages:impersonation.searchPlaceholder", "Search users...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-2">
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">
                {t("common:loading", "Loading...")}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                {searchQuery
                  ? t("pages:impersonation.noResults", "No users found")
                  : t("pages:impersonation.noUsers", "No users available")}
              </div>
            ) : (
              filteredUsers.map((user) => {
                const roleLabel = getRoleLabel(user.role, (k, defaultValue) =>
                  t(k, { defaultValue })
                );
                return (
                  <div
                    key={user.id}
                    className="group flex cursor-pointer items-center justify-between rounded-lg border bg-card p-3 transition-colors hover:bg-accent/50"
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {getDisplayName(user) || user.email || user.id}
                        </div>
                        {roleLabel !== "Location User" && (
                          <div className="truncate text-sm text-muted-foreground">{user.email}</div>
                        )}
                        <div className="mt-1 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {roleLabel}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      {t("pages:impersonation.select", "Select")}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        <div className="flex items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
          <Shield className="h-3 w-3" />
          {t(
            "pages:impersonation.securityNote",
            "All actions are logged. Only users in your account are shown."
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
